import { API, graphqlOperation } from "aws-amplify";
import { getUser } from "../graphql/queries";

/**
 * Get all pricing adjustments for invoices (group + provider)
 * This function combines group and provider offsets into a single percentage
 * Personal offsets (user.priceOffsetType/value) are NOT included for invoicing
 * @param {Object} userRecord - The user record
 * @returns {Object} Pricing configuration with combined adjustments
 */
export const getInvoicePricingConfig = async (userRecord) => {
  try {
    let adjustments = [];

    // Fetch group adjustments if user has a group assignment
    if (userRecord.userGroupID && userRecord.userGroup) {
      const group = userRecord.userGroup;

      if (group && (group.percentageAdjustment || group.dollarAdjustment)) {
        adjustments.push({
          id: group.id,
          title: group.title,
          percentageAdjustment: group.percentageAdjustment || 0,
          dollarAdjustment: group.dollarAdjustment || 0,
          adjustmentType: group.adjustmentType || "percentage",
        });
        console.log(
          "Found group pricing for invoice:",
          group.title,
          group.percentageAdjustment,
        );
      }
    }

    // Fetch provider offsets if user has a provider (createdByUserId)
    if (
      userRecord.createdByUserId &&
      userRecord.createdByUserId !== "deleted"
    ) {
      try {
        const providerResult = await API.graphql(
          graphqlOperation(getUser, { id: userRecord.createdByUserId }),
        );
        const provider = providerResult.data.getUser;

        if (
          provider &&
          provider.priceOffsetType &&
          provider.priceOffsetValue !== null &&
          provider.priceOffsetValue !== undefined
        ) {
          adjustments.push({
            id: "provider",
            title: `Provider Pricing (${provider.companyName || provider.email || "Provider"})`,
            percentageAdjustment:
              provider.priceOffsetType === "percentage"
                ? provider.priceOffsetValue
                : 0,
            dollarAdjustment:
              provider.priceOffsetType === "fixed"
                ? provider.priceOffsetValue
                : 0,
            adjustmentType: provider.priceOffsetType,
          });
          console.log(
            "Found provider pricing for invoice:",
            provider.companyName || provider.email,
            provider.priceOffsetValue,
          );
        }
      } catch (providerError) {
        console.error(
          "Error fetching provider pricing for invoice:",
          providerError,
        );
      }
    }

    // Combine all adjustments into a single percentage
    if (adjustments.length > 0) {
      // Calculate combined percentage (sum of all percentage adjustments)
      const totalPercentage = adjustments.reduce((sum, adj) => {
        if (adj.adjustmentType === "percentage" || !adj.adjustmentType) {
          return sum + parseFloat(adj.percentageAdjustment || 0);
        }
        return sum;
      }, 0);

      // Check if there are dollar adjustments
      const hasDollarAdjustments = adjustments.some(
        (adj) => adj.dollarAdjustment && adj.dollarAdjustment !== 0,
      );

      console.log(
        `Combined invoice pricing adjustments: ${totalPercentage}% (from ${adjustments.length} sources)`,
      );

      return {
        type: adjustments.length > 0 ? "combined" : "none",
        adjustments: adjustments,
        combinedPercentage: totalPercentage,
        hasProviderPricing: adjustments.some((adj) => adj.id === "provider"),
        hasGroupPricing: adjustments.some((adj) => adj.id !== "provider"),
        hasDollarAdjustments: hasDollarAdjustments,
        hasIndividualPricing: false,
      };
    } else {
      // No pricing adjustments
      console.log("No pricing adjustments found for invoice");
      return {
        type: "none",
        adjustments: [],
        combinedPercentage: 0,
        hasProviderPricing: false,
        hasGroupPricing: false,
        hasDollarAdjustments: false,
        hasIndividualPricing: false,
      };
    }
  } catch (error) {
    console.error("Error getting invoice pricing config:", error);
    return {
      type: "none",
      adjustments: [],
      combinedPercentage: 0,
      hasProviderPricing: false,
      hasGroupPricing: false,
      hasDollarAdjustments: false,
      hasIndividualPricing: false,
    };
  }
};

/**
 * Apply pricing adjustments to a price (handles both percentage and dollar adjustments)
 * Uses simple addition for percentage adjustments (not compound discount)
 * @param {number} basePrice - The original price
 * @param {Array} adjustments - Array of adjustments (can include both group and provider)
 * @returns {number} The final adjusted price
 */
export const applyInvoiceAdjustments = (basePrice, adjustments = []) => {
  let price = parseFloat(basePrice);

  if (isNaN(price)) {
    console.warn("Invalid base price:", basePrice);
    return basePrice;
  }

  // Combine all percentage adjustments first (simple addition)
  let totalPercentageAdjustment = 0;
  let totalDollarAdjustment = 0;

  adjustments.forEach((adjustment) => {
    if (
      adjustment.adjustmentType === "percentage" ||
      !adjustment.adjustmentType
    ) {
      if (adjustment.percentageAdjustment) {
        totalPercentageAdjustment += parseFloat(
          adjustment.percentageAdjustment,
        );
      }
    }

    if (adjustment.dollarAdjustment) {
      totalDollarAdjustment += parseFloat(adjustment.dollarAdjustment);
    }
  });

  // Apply combined percentage adjustment once (simple addition)
  if (totalPercentageAdjustment !== 0) {
    price = price * (1 + totalPercentageAdjustment / 100);
  }

  // Apply combined dollar adjustment
  if (totalDollarAdjustment !== 0) {
    price = price + totalDollarAdjustment;
  }

  return Math.round(price * 100) / 100; // Round to 2 decimal places
};

/**
 * Process a price with combined adjustments for invoice use
 * @param {number} basePrice - The base price
 * @param {Object} userRecord - The user record to get adjustments from
 * @returns {number} The adjusted price
 */
export const calculateInvoicePrice = async (basePrice, userRecord) => {
  try {
    const pricingConfig = await getInvoicePricingConfig(userRecord);

    if (pricingConfig.adjustments.length > 0) {
      return applyInvoiceAdjustments(basePrice, pricingConfig.adjustments);
    }

    return basePrice;
  } catch (error) {
    console.error("Error calculating invoice price:", error);
    return basePrice;
  }
};

/**
 * Get pricing summary for invoice display purposes
 * @param {number} originalPrice - Original price
 * @param {number} finalPrice - Final adjusted price
 * @param {Array} adjustments - Applied adjustments
 * @returns {Object} Pricing summary object
 */
export const getInvoicePricingSummary = (
  originalPrice,
  finalPrice,
  adjustments = [],
) => {
  const totalAdjustment = finalPrice - originalPrice;
  const adjustmentPercentage =
    originalPrice > 0 ? (totalAdjustment / originalPrice) * 100 : 0;

  return {
    originalPrice,
    finalPrice,
    totalAdjustment,
    adjustmentPercentage,
    hasAdjustments: adjustments.length > 0,
    appliedAdjustments: adjustments.map((adj) => adj.title).join(", "),
    adjustmentType: adjustments.length > 0 ? "combined" : "none",
  };
};
